// GuidedTour — role-aware product tour built on driver.js (v1 API).
//
// The Sidebar Drawer is temporary and never opened in this app (no trigger
// exists), so tour steps anchor ONLY to always-visible elements — the Header
// icon buttons and each individual Dashboard card — rather than opening the
// drawer. The tour walks one card at a time. This guarantees every highlight
// has a visible target and avoids driver.js pointing at an off-screen or
// unmounted element.
//
// driver.js ships its own type declarations in v1, so no @types package is
// needed. The whole build + drive is wrapped in try/catch so a tour failure can
// never crash the app.

import { driver, DriveStep, Side, Alignment } from 'driver.js';
import 'driver.js/dist/driver.css';
import { TOUR, TourKey } from './tourTargets';

export interface TourArgs {
  role: string;
  isPhotographyOnly: boolean;
  isAdmin: boolean;
}

export interface StartTourArgs extends TourArgs {
  // Called whenever the tour ends (finish, skip, Escape, overlay click) so the
  // completed flag is set however the tour is dismissed.
  onFinish?: () => void;
}

// A master step entry. `show` mirrors the EXACT component gating; `selector`
// targets the real on-screen element via its data-tour attribute.
interface MasterStep {
  key: TourKey;
  selector: string;
  popover: { title: string; description: string; side?: Side; align?: Alignment };
  show: (args: TourArgs) => boolean;
}

const sel = (key: TourKey) => `[data-tour="${key}"]`;

// MASTER list. The tour walks each Dashboard CARD individually (not whole
// sections), the same way the Image Generation card was always highlighted on
// its own. `show` predicates mirror the EXACT component gating:
//  - HEADER_ACTIONABLES: header shows it inside {!isPhotographyOnly}.
//  - Daily Workflow cards (Add / Reduce / Carding / Cyclic Count) render only in
//    the non-photography Dashboard branch.
//  - Catalog cards render only in the non-photography Dashboard branch.
//  - CARD_IMAGE_GEN: photography-only sees the sole card; admins see it in Catalog
//    (catalogInsights appends it for isAdmin && !isPhotographyOnly, and the
//    photography-only branch renders it for isPhotographyOnly) → shown for
//    isPhotographyOnly || isAdmin.
//  - Admin cards (Manage users / System settings) mirror showAdminTools = isAdmin && !isPhotographyOnly.
//  - HEADER_USER_MENU is always present (final step) — Settings, the tour, and What's New live there.
//
// The DOM-presence filter in buildTourSteps() drops any step whose card is not
// actually mounted, so these predicates can stay simple and the tour never
// points at a missing element.
const notPhotography = (a: TourArgs) => !a.isPhotographyOnly;
const adminOnly = (a: TourArgs) => a.isAdmin && !a.isPhotographyOnly;

const MASTER_STEPS: MasterStep[] = [
  {
    key: TOUR.HEADER_ACTIONABLES,
    selector: sel(TOUR.HEADER_ACTIONABLES),
    popover: {
      title: 'Actionables',
      description: 'Your open tasks live here — the badge shows how many need attention.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  // ── Daily Workflow cards ──────────────────────────────────────────────────
  {
    key: TOUR.CARD_ADD_BUNDLE,
    selector: sel(TOUR.CARD_ADD_BUNDLE),
    popover: {
      title: 'Add bundle inventory',
      description: 'Start the bundle creation workflow — the main way you build bundle stock.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_REDUCE_BUNDLE,
    selector: sel(TOUR.CARD_REDUCE_BUNDLE),
    popover: {
      title: 'Reduce bundle inventory',
      description: 'Break bundles back into their child SKUs.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_CARDING,
    selector: sel(TOUR.CARD_CARDING),
    popover: {
      title: 'Carding',
      description: 'Move SKUs from NONCARDED shelves to carded, deducting packaging automatically.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_CYCLIC_COUNT,
    selector: sel(TOUR.CARD_CYCLIC_COUNT),
    popover: {
      title: 'Cyclic count',
      description: 'Physical inventory counts with variance detection and Unicommerce adjustment.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  // ── Catalog cards ─────────────────────────────────────────────────────────
  {
    key: TOUR.CARD_BUNDLE_SKUS,
    selector: sel(TOUR.CARD_BUNDLE_SKUS),
    popover: {
      title: 'Bundle SKUs',
      description: 'Manage bundle SKUs and their child mappings.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_BUNDLE_COMBINATION,
    selector: sel(TOUR.CARD_BUNDLE_COMBINATION),
    popover: {
      title: 'Bundle combination',
      description: 'AI-powered bundle suggestions based on DRR, inventory and sales data.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_VIEW_INVENTORY,
    selector: sel(TOUR.CARD_VIEW_INVENTORY),
    popover: {
      title: 'View inventory',
      description: 'Check current stock levels across your SKUs.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_SAVED_DRAFTS,
    selector: sel(TOUR.CARD_SAVED_DRAFTS),
    popover: {
      title: 'Saved drafts',
      description: 'Resume or manage partially completed bundles.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_RECENT_ACTIVITIES,
    selector: sel(TOUR.CARD_RECENT_ACTIVITIES),
    popover: {
      title: 'Recent activities',
      description: 'View recent child-deductions and bundle creations.',
      side: 'bottom',
      align: 'start',
    },
    show: notPhotography,
  },
  {
    key: TOUR.CARD_IMAGE_GEN,
    selector: sel(TOUR.CARD_IMAGE_GEN),
    popover: {
      title: 'Image generation',
      description: 'Generate AI product photography composites.',
      side: 'top',
      align: 'start',
    },
    show: (a) => a.isPhotographyOnly || a.isAdmin,
  },
  // ── Admin cards ───────────────────────────────────────────────────────────
  {
    key: TOUR.CARD_MANAGE_USERS,
    selector: sel(TOUR.CARD_MANAGE_USERS),
    popover: {
      title: 'Manage users',
      description: 'Create, edit, or delete application users.',
      side: 'bottom',
      align: 'start',
    },
    show: adminOnly,
  },
  {
    key: TOUR.CARD_SYSTEM_SETTINGS,
    selector: sel(TOUR.CARD_SYSTEM_SETTINGS),
    popover: {
      title: 'System settings',
      description: 'Manage WMS configurations and excluded shelves.',
      side: 'bottom',
      align: 'start',
    },
    show: adminOnly,
  },
  {
    key: TOUR.HEADER_USER_MENU,
    selector: sel(TOUR.HEADER_USER_MENU),
    popover: {
      title: 'Your account',
      description: "Your account, settings, this tour, and What's New live here.",
      side: 'bottom',
      align: 'end',
    },
    show: () => true,
  },
];

/**
 * Build the ordered list of driver.js steps for the given role. Steps are kept
 * only when BOTH their role predicate passes AND their target is actually in the
 * DOM — the querySelector presence filter drops any step whose element is not
 * mounted, so driver.js never shows a centered orphan popover.
 */
export function buildTourSteps(args: TourArgs): DriveStep[] {
  return MASTER_STEPS.filter(
    (step) => step.show(args) && document.querySelector(step.selector) != null,
  ).map((step) => ({
    element: step.selector,
    popover: {
      title: step.popover.title,
      description: step.popover.description,
      side: step.popover.side,
      align: step.popover.align,
    },
  }));
}

/**
 * Build and start the tour. Safe to call regardless of the completed flag — the
 * auto-start effect is what checks `salty_tourCompleted`; this function always
 * runs so the Header "Take a tour" replay works. If no steps survive filtering
 * (e.g. a role with nothing on screen yet), it returns without starting. Any
 * error is caught and only logged so a tour failure never crashes the app.
 */
export function startTour(args: StartTourArgs): void {
  try {
    const steps = buildTourSteps(args);
    if (steps.length === 0) return;

    let finished = false;
    const runFinish = () => {
      if (finished) return;
      finished = true;
      try {
        args.onFinish?.();
      } catch (err) {
        console.warn('Tour onFinish callback failed:', err);
      }
    };

    const d = driver({
      showProgress: true,
      allowClose: true,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      steps,
      // Fires on every exit path: finish (Done), skip/close (x), Escape, and
      // overlay click — so the completed flag is set however the tour ends.
      onDestroyed: () => {
        runFinish();
      },
    });

    d.drive();
  } catch (err) {
    // Never rethrow — a tour error must not take down the app.
    console.warn('Guided tour failed to start:', err);
  }
}
